import { FC, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import {
  selectActiveModelsByBrandState,
  selectModelLoading,
  selectModelsLoadError,
} from "../../store/selectors/models";
import { fetchModels } from "../../store/models/slice";
import ModelsLayout from "../../layout/ModelsLayout/ModelsLayout";
import HeaderBrandsSelector from "../../components/HeaderBrandsSelector/HeaderBrandsSelector";
import { ERoutes } from "../../constants/routes";
import {
  selectActiveBrandsState,
  selectBrandState,
  selectSelectedBrandState,
} from "../../store/selectors/brands";
import Title from "../../components/Title/Title";
import { fetchDailyAverageModel } from "../../store/scores/slice";
import { setViewBrandId } from "../../store/brands/slice";
import CardWrapper from "../../components/CardWrapper/CardWrapper";
import {
  selectBrandEveryDay7dSummaryForViewBrand,
  selectLastUpdateTimeState,
} from "../../store/selectors/scores";
import { upgradeSubscription } from "../../store/auth/slice";
import CardModel from "../../components/CardModel/CardModel";

import styles from "./Models.module.css";

const ModelsView: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const brand = useSelector(selectBrandState);
  const brands = useSelector(selectActiveBrandsState);
  const models = useSelector(selectActiveModelsByBrandState(id));
  const modelsLoading = useSelector(selectModelLoading);
  const modelsError = useSelector(selectModelsLoadError);
  const widgetSummary = useSelector(selectBrandEveryDay7dSummaryForViewBrand);

  const selectedBrand = useSelector(selectSelectedBrandState);
  const lastUpdateTime = useSelector(selectLastUpdateTimeState(Number(id)));

  const selectedBrandId = selectedBrand?.id ?? null;

  useEffect(() => {
    if (selectedBrandId == null) return;
    dispatch(fetchDailyAverageModel({ brand_id: selectedBrandId, days: 16 }));
  }, [selectedBrandId, dispatch]);

  useEffect(() => {
    if (id && brands) {
      const changedBrand = brands.find(brand => brand.id == Number(id));
      if (!changedBrand && brand) {
        navigate(ERoutes.Models.replace(":id", String(brand.id)), { replace: true });
      }
      dispatch(setViewBrandId((changedBrand || brand)?.id ?? null));
    }

    if (!id) {
      if (brand) {
        dispatch(setViewBrandId(brand?.id ?? null));
        navigate(ERoutes.Models.replace(":id", String(brand.id)), { replace: true });
      }

      if (selectedBrand) {
        navigate(ERoutes.Models.replace(":id", String(selectedBrand.id)), { replace: true });
      }
    }
  }, [id, brands]);

  const handleChangeBrands = (brandId: number) => {
    navigate(ERoutes.Models.replace(":id", String(brandId)), { replace: true });
  };

  const handleUpgrade = () => {
    dispatch(upgradeSubscription());
  }


  return (
    <ModelsLayout>
      <HeaderBrandsSelector
        className={styles['models__header']}
        onBrandChange={handleChangeBrands}
        score={widgetSummary}
        lastUpdateTime={lastUpdateTime}
      />

      <Title
        className={styles["models__title"]}
        label="Active AI Models"
      />

      <CardWrapper
        onUpgrade={handleUpgrade}
        items={models}
        skeletonCount={3}
        currentBrandId={id}
        component={CardModel}
        loading={modelsLoading}
        error={modelsError}
        onRetry={() => dispatch(fetchModels())}
      />
    </ModelsLayout>
  );
};


export default ModelsView;